/*!
 * American Well Online Care
 *
 * Copyright © 2021 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { AppointmentFhirData } from '@app/shared/model/fhir/appointment-fhir-data.model';
import { EncounterFhirData, EncounterFhirDataDetail } from '@app/shared/model/fhir/encounter-fhir-data.model';
import { PatientFhirData } from '@app/shared/model/fhir/patient-fhir-data.model';
import { PractitionerFhirData } from '@app/shared/model/fhir/practitioner-fhir-data.model';
import { Telecom } from '@app/shared/model/fhir/telecom.model';
import { EnvConfigService } from '@app/shared/service/env-config.service';
import { PractitionerRoleFhirData } from '../model/fhir/practitioner-role-fhir.data.model';
import { Logger } from 'loglevel';
import { LOGGER_TOKEN } from '@app/shared/service/logger-token';
import { ApmService } from '@app/apm-module';

export const PRACTITIONER_PROFILE_IMAGE_TYPE_CODE = '18775-7';
export const FHIR_ENDPOINTS = { CDR: 'v2/cdr' };
export const FHIR_CDR_ENDPOINTS = { DOCUMENT_REFERENCE: 'DocumentReference' };

@Injectable({
  providedIn: 'root'
})
export class FhirProxyService {
  private httpHeaders: HttpHeaders;

  constructor(
    private http: HttpClient,
    private envConfigService: EnvConfigService,
    private apm: ApmService,
    @Inject(LOGGER_TOKEN) public log: Logger) {
    const bearerToken = sessionStorage.getItem('aw-bearer-token');
    this.httpHeaders = new HttpHeaders({
      'Content-type': 'application/json',
      Authorization: `Bearer ${bearerToken}`
    });
  }

  /**
   * Calls fhir-proxy service to retrieve relevant patient FHIR data.
   *
   * @param patientFhirReference the patient FHIR reference
   * @returns the patient FHIR data
   */
  public async getPatientData(patientReference: string): Promise<PatientFhirData> {
    this.log.info('Entering getPatientData');
    if (!patientReference) {
      throw new Error('Internal Validation Error: patientFhirId is undefined or null');
    }
    const sessionPatientFhirData = sessionStorage.getItem(patientReference);
    if (sessionPatientFhirData) {
      console.debug('Using session patientFhirData');
      return JSON.parse(sessionPatientFhirData);
    }
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}/${patientReference}`;
    const patientResource: any = await this.http.get<PatientFhirData>(fhirUrl, { headers: this.httpHeaders }).toPromise();
    const name: any = this.parseName(patientResource.name);
    const patientFhirData = {
      reference: patientReference,
      displayName: this.parseDisplayName(patientResource.name),
      mobilePhone: patientResource.telecom ? this.extractPhoneNumber(patientResource.telecom) : null,
      firstName: name?.firstName,
      lastName: name?.lastName,
      dateOfBirth: patientResource.birthDate,
      gender: patientResource.gender,
      addresses: patientResource.address,
      phoneNumbers: this.parseTelecoms(patientResource.telecom, 'phone'),
      emailAddresses: this.parseTelecoms(patientResource.telecom, 'email'),
      preferredLanguage: this.parsePreferredLanguage(patientResource.communication),
      medicalRecordNumber: patientResource.identifier?.find((item: any) => item.type?.text === 'MRN' || item.type?.text === 'Medical Record Number')?.value,
      externalUserId: patientResource.identifier?.find((item: any) => item.type?.text === 'WPRINTERNAL')?.value
    };
    sessionStorage.setItem(patientReference, JSON.stringify(patientFhirData));
    console.debug('Leaving getPatientData');
    return patientFhirData;
  }

  /**
   * Calls fhir-proxy service to retrieve relevant practitioner FHIR data.
   *
   * @param practitionerReference the practitioner FHIR reference
   * @returns the practitioner FHIR data
   */
  public async getPractitionerData(practitionerReference: string): Promise<PractitionerFhirData> {
    this.log.debug('Entering getPractitionerData');
    if (!practitionerReference) {
      throw new Error('Internal Validation Error: practitionerReference is undefined or null');
    }
    const sessionPractitionerFhirData = sessionStorage.getItem(practitionerReference);
    if (sessionPractitionerFhirData) {
      console.debug('Using session practitionerFhirData');
      return JSON.parse(sessionPractitionerFhirData);
    }
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}/${practitionerReference}`;
    const practitionerResource: any = await this.http.get<PractitionerFhirData>(fhirUrl, { headers: this.httpHeaders })
      .toPromise();

    const practitionerFhirData = {
      id: practitionerResource.id,
      displayName: this.parseDisplayName(practitionerResource.name)
    };

    sessionStorage.setItem(practitionerReference, JSON.stringify(practitionerFhirData));
    console.debug('Leaving getPractitionerData');
    return practitionerFhirData;
  }

  /**
   * Calls fhir-proxy service to retrieve relevant practitioner role FHIR data.
   *
   * @param practitionerReference the practitioner FHIR reference
   * @returns the practitioner role FHIR data
   */
   public async getPractitionerRoleData(practitionerReference: string): Promise<PractitionerRoleFhirData> {
    const practitionerRoleReference = `PractitionerRole/${practitionerReference}`;
    console.debug('Entering getPractitionerRoleData', practitionerRoleReference);
    if (!practitionerReference) {
      throw new Error('Internal Validation Error: practitionerReference is undefined or null');
    }
    const sessionPractitionerRoleFhirData = sessionStorage.getItem(practitionerRoleReference);
    if (sessionPractitionerRoleFhirData) {
      console.debug('Using session practitionerFhirData');
      return JSON.parse(sessionPractitionerRoleFhirData);
    }

    const practitionerId = practitionerReference.split('Practitioner/')[1];
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}/PractitionerRole?practitioner=${practitionerId}`;
    const practitionerRoleResource: any = await this.http.get<PractitionerRoleFhirData>(fhirUrl, { headers: this.httpHeaders })
      .toPromise();

    const practitionerRoleFhirData = {
      id: practitionerRoleResource.id,
      specialties: this.parseSpecialties(practitionerRoleResource),
      organizations: this.parseOrganization(practitionerRoleResource)
    };

    sessionStorage.setItem(practitionerRoleReference, JSON.stringify(practitionerRoleFhirData));
    console.debug('Leaving getPractitionerRoleData');
    return practitionerRoleFhirData;
  }

  /**
   * Calls fhir-proxy service to retrieve relevant encounter FHIR data.
   *
   * @param encounterReference the encounter FHIR reference
   * @returns the encounter FHIR data if successful, otherwise an error
   */
  public async getEncounterData(encounterReference: string): Promise<EncounterFhirData | Error> {
    this.log.debug('Entering getEncounterFhirData');
    if (!encounterReference) {
      throw new Error('Internal Validation Error: encounterReference is undefined or null');
    }
    const sessionEncounterFhirData = sessionStorage.getItem(`encounter-${encounterReference}`);
    if (sessionEncounterFhirData) {
      console.debug('Using session encounterFhirData');
      return JSON.parse(sessionEncounterFhirData);
    }
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}/${encounterReference}`;
    const encounterResource: any = await this.http.get(fhirUrl, { headers: this.httpHeaders }).toPromise();
    const encounterFhirData: EncounterFhirData = new EncounterFhirData();
    if (encounterResource.subject?.reference) {
      await this.getPatientData(encounterResource.subject.reference)
        .then((patientFhirData: PatientFhirData) => {
          encounterFhirData.patient = patientFhirData;
        })
        .catch(error => {
          console.error('Error occurred fetching patient FHIR data');
          this.log.error('Error occurred fetching patient FHIR data', error);
          this.apm.apmBase.captureError({
            message: 'Error occurred fetching patient FHIR data',
            ...error
          });
          return;
        });
    }
    const practitionerReference = encounterResource.participant[0]?.individual?.reference;
    if (practitionerReference) {
      await this.getPractitionerData(practitionerReference)
        .then((practitionerFhirData) => {
          encounterFhirData.practitioner = practitionerFhirData;
        })
        .catch(error => {
          console.error('Error occurred fetching participant FHIR data');
          this.log.error('Error occurred fetching participant FHIR data', error);
          this.apm.apmBase.captureError({
            message: 'Error occurred fetching participant FHIR data',
            ...error
          });
          return;
        });
    }
    sessionStorage.setItem(`encounter-${encounterReference}`, JSON.stringify(encounterFhirData));
    console.debug('Leaving getEncounterFhirData');
    return encounterFhirData;
  }

  /**
   * Calls fhir-proxy service to retrieve the encounter details.
   *
   * @param encounterReference the encounter FHIR reference
   * @returns the patient FHIR data if successful.
   */
  public async getEncounterDetails(externalEncounterId: string): Promise<EncounterFhirDataDetail> {
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}/Encounter/${externalEncounterId}`;
    try {
      const encounterDetails: any = await this.http.get(fhirUrl, { headers: this.httpHeaders }).toPromise();
      const serviceLineContext = encounterDetails?.type[0]?.coding[0].display;
      const location = encounterDetails?.location[0]?.location.display;

      return {serviceLineContext, location};
    } catch (error) {
      console.error('Error occurred encounter detail FHIR data');
      this.log.error('Error occurred encounter detail data', error);
      this.apm.apmBase.captureError({
        message: 'Error occurred encounter detail FHIR data',
        ...error
      });
    }
  }

  /**
   * Calls fhir-proxy service to retrieve relevant appointment FHIR data.
   *
   * @param appointmentReference the appointment FHIR reference
   * @returns the appointment FHIR data if successful, otherwise an error
   */
  public async getAppointmentData(appointmentReference: string): Promise<AppointmentFhirData | Error> {
    this.log.debug('Entering getAppointmentFhirData');
    if (!appointmentReference) {
      throw new Error('Internal Validation Error: appointmentReference is undefined or null');
    }
    const sessionAppointmentFhirData = sessionStorage.getItem(`appointment-${appointmentReference}`);
    if (sessionAppointmentFhirData) {
      console.debug('Using session appointmentFhirData');
      return JSON.parse(sessionAppointmentFhirData);
    }
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}/${appointmentReference}`;
    const appointmentResource: any = await this.http.get(fhirUrl, { headers: this.httpHeaders }).toPromise();
    const patientReference: string = appointmentResource.participant
      .find((item: any) => item.actor.reference?.includes('Patient') === true)?.actor?.reference;
    const practitionerActor = appointmentResource.participant
      .find((item: any) => item.actor.reference?.includes('Practitioner') === true)?.actor;
    const appointmentFhirData: AppointmentFhirData = new AppointmentFhirData();
    if (patientReference) {
      await this.getPatientData(patientReference.substring(patientReference.lastIndexOf('Patient')))
        .then((patientFhirData: PatientFhirData) => {
          appointmentFhirData.patient = patientFhirData;
        })
        .catch(error => {
          console.error('Error occurred fetching patient FHIR data');
          this.log.error('Error occurred fetching patient FHIR data', error);
          this.apm.apmBase.captureError({
            message: 'Error occurred fetching patient FHIR data',
            ...error
          });
          return;
        });
    }
    if (practitionerActor?.display) {
      appointmentFhirData.practitioner.displayName = practitionerActor.display;
    } else if (practitionerActor?.reference) {
      const practitionerReference: string = practitionerActor.reference.substring(practitionerActor.reference.lastIndexOf('Practitioner'));
      await this.getPractitionerData(practitionerReference)
        .then((practitionerFhirData) => {
          appointmentFhirData.practitioner = practitionerFhirData;
        })
        .catch(error => {
          console.error('Error occurred fetching practitioner FHIR data');
          this.log.error('Error occurred fetching practitioner FHIR data', error);
          this.apm.apmBase.captureError({
            message: 'Error occurred fetching practitioner FHIR data',
            ...error
          });
          return;
        });
    }
    sessionStorage.setItem(`appointment-${appointmentReference}`, JSON.stringify(appointmentFhirData));
    console.debug('Leaving getAppointmentFhirData');
    return appointmentFhirData;
  }

  /**
   * Calls fhir-proxy service to retrieve relevant practitioner and patient FHIR data
   *
   * @param practitionerReference the practitioner FHIR reference
   * @param patientReference the patient FHIR reference
   * @returns the practitioner and patient FHIR data
   */
  public async getPatientProviderFhirData(
    practitionerReference: string,
    patientReference: string,
    showPractitionerSpecialty: boolean = false,
    showPractitionerAvatar: boolean = false
  ): Promise<{
    practitionerFhirData: { id: string, displayName: string },
    patientFhirData: { firstName: string },
    practitionerRoleFhirData: PractitionerRoleFhirData,
    practitionerProfileImageUrl: string
  }> {
    const [practitioner, patient, practitionerRole, practitionerProfileImageUrl] = await Promise.all([
      this.getPractitionerData(practitionerReference)
        .catch(error => {
          console.error('Unable to get practitioner fhir data.');
          this.log.error('Unable to get practitioner fhir data: ', error);
          this.apm.apmBase.captureError({
            message: 'Unable to get practitioner fhir data',
            ...error
          });
          return undefined;
        }),
      this.getPatientData(patientReference)
        .catch(error => {
          console.error('Unable to get patient fhir data');
          this.log.error('Unable to get patient fhir data: ', error);
          this.apm.apmBase.captureError({
            message: 'Unable to get patient fhir data',
            ...error
          });
          return undefined;
        }),

      showPractitionerSpecialty && this.getPractitionerRoleData(practitionerReference)
        .catch(error => {
          console.warn('Unable to get practitioner role fhir data: ', error);
          this.apm.apmBase.captureError({
            message: 'Unable to get practitioner role fhir data',
            ...error
          });
          return undefined;
        }),

      showPractitionerAvatar && this.getPractitionerProfileImageUrl(practitionerReference)
        .catch(error => {
          console.warn('Unable to get practitioner image: ', error);
          this.apm.apmBase.captureError({
            message: 'Unable to get practitioner image',
            ...error
          });
          return undefined;
        }),
    ]);

    const practitionerFhirData = practitioner?.displayName ?
      practitioner : {
        id: '',
        displayName: this.envConfigService.getSettings().practitionerDisplayName
      };

    const patientFhirData = patient?.firstName ?
      { firstName: patient.firstName } : { firstName: '' };

    const practitionerRoleFhirData = practitionerRole?.id ? practitionerRole : {
      specialties: [''],
      organizations: [ '' ]
    };

    return {
      practitionerFhirData,
      patientFhirData,
      practitionerRoleFhirData,
      practitionerProfileImageUrl
    };
  }

  /**
   * Extracts a phone number
   *
   * @param telecom the FHIR telecom value
   * @returns the mobile number
   */
  private extractPhoneNumber(telecom: Telecom[]): string {
    const phones = telecom && telecom.length !== 0 ? telecom.filter(value => value.system === 'phone') : null;
    if (phones?.length !== 0) {
      const mobile = phones.find(value => value.use === 'mobile');
      if (mobile) {
        return mobile.value;
      }
      const home = phones.find(value => value.use === 'home');
      return home ? home.value : phones[0].value;
    }
    return '';
  }

  /**
   * Parses the display name
   *
   * @param nameElement the FHIR name value
   * @returns the resource display name
   */
  private parseDisplayName(nameElement: any): string {
    let displayName = nameElement.text;
    if (!displayName && nameElement instanceof Array && nameElement.length >= 1) {
      // Look for name with a use of 'usual'. If not found, then pick the first name
      const usualName = nameElement.find((item: any) => item.use === 'usual');
      displayName = usualName?.text ?? nameElement[0].text;
      // Construct the name, if not found in the text property
      if (!displayName) {
        const nameCandidate: any = usualName ?? nameElement[0];
        displayName = Array.isArray(nameCandidate.prefix) ?
          `${nameCandidate.prefix[0]} ${nameCandidate.given[0]} ${nameCandidate.family ?? nameCandidate.family[0]}`
          : `${nameCandidate.given[0]} ${nameCandidate.family ?? nameCandidate.family[0]}`;
      }
    }

    return displayName;
  }

  /**
   * Parses the name
   *
   * @param nameElement the FHIR name value
   * @returns the resource name
   */
  private parseName(nameElement: any): {} {
    let name;
    if (nameElement instanceof Array && nameElement.length >= 1) {
      const usualName = nameElement.find((item: any) => item.use === 'usual' || item.use === 'official');
      if (usualName) {
        name = {
          firstName: usualName.given[0],
          lastName: usualName.family,
        };
      }
    }
    return name;
  }

  /**
   * Parses the name
   *
   * @param communicationElement the FHIR communication value
   * @returns the preferred language
   */
  private parsePreferredLanguage(communicationElement: any): any {
    let preferredLanguage;
    if (communicationElement instanceof Array && communicationElement.length >= 1) {
      preferredLanguage = communicationElement.find((item: any) => item.preferred === true);
    }
    return preferredLanguage?.language?.coding[0] ?? null;
  }

  /**
   * Parses the FHIR telecoms
   *
   * @param telecoms the FHIR telecoms
   * @param systemValue the FHIR system value. Example: phone, fax, email etc
   * @returns the FHIR telecoms for the given system value
   */
  private parseTelecoms(telecoms: any, systemValue: string): any {
    return (telecoms?.length > 0) ? telecoms.filter(value => value.system === systemValue) : null;
  }

  /**
   * Parses the practitioner role specialties
   *
   * @param practitionerRoleResource the practitioner role resource
   * @returns the specialties
   */
  parseSpecialties(practitionerRoleResource: any): string[] {
    const specialties: string[] = [];
    const entries = practitionerRoleResource.entry;

    if (entries) {
      entries.forEach(entry => {
        const specialty = entry?.resource?.specialty[0]?.text;

        if (specialty) {
          specialties.push(specialty);
        }
      });
    }

    return specialties;
  }

  /**
   * Parses the practitioner organization
   *
   * @param practitionerRoleResource the practitioner role resource
   * @returns organization
   */
  parseOrganization(practitionerRoleResource: any): any {
    const entries = practitionerRoleResource.entry;
    const organizations = [];

    if (entries) {
      entries.forEach((entry) => {
        const organizationName = entry?.resource?.organization?.display;

        if (organizationName) {
          organizations.push(organizationName);
        }
      });
    }

    return organizations;
  }

  private async getPractitionerProfileImageUrl(practitionerReference: string): Promise<string> {
    const fhirUrl = `${this.envConfigService.getSettings().fhirProxyUrl}`;
    const documentReferenceFhirCdrEndpoint =
      `${fhirUrl}/${FHIR_ENDPOINTS.CDR}/${FHIR_CDR_ENDPOINTS.DOCUMENT_REFERENCE}`;

    const params = {
      subject: practitionerReference.split('Practitioner/')[1],
      typeCode: PRACTITIONER_PROFILE_IMAGE_TYPE_CODE
    };

    const entityResource = await this.http.get<any>(
      documentReferenceFhirCdrEndpoint,
      {
        headers: this.httpHeaders,
        params
      }).toPromise();

    const practitionerImageResource =
      entityResource.entry.find((entry: Record<string, any>) => {
        const typeCode = entry.resource.type.coding[0].code;
        return typeCode === PRACTITIONER_PROFILE_IMAGE_TYPE_CODE;
      });

    return practitionerImageResource?.resource.content[0].attachment.url;
  }
}
